import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

import router from "core/router";
import user from "core/user";
import useFormRequest from "core/hookForm/hooks/useFormRequest";
import userContracts from "core/userContracts";
import { useHasRole } from "core/hooks";
import { getIsVerifiedUser } from "core/userInfo/selectors";
import { PermissionTypes, Roles } from "enums";
import { Pages, Tabs } from "routeConstants";
import vehicleDataChange, { VehicleDataSubmitDto } from "serverApi/vehicleDataChange";

import {
    setIsFormLoading,
    setIsLicencePlateFillInLoading,
    setIsSubmitting,
    setIsVehicleLicenceFillInLoading,
    setStep,
} from "../../../actions";
import { getIsFormLoading, getIsSubmitting, getPolicyHolder, getStep, getVehicles } from "../../../selectors";
import getValidationSchema from "../validationSchema";

export type VehicleDataChangeFormValues = {
    errors?: string[];
    idChange?: string;
    vinNumber?: string;
    validityStartDate?: Date;
    isLicencePlateNumberChange?: boolean;
    isVehicleLicenceNumberChange?: boolean;
    oldLicencePlateNumber?: string;
    newLicencePlateNumber?: string;
    oldVehicleLicenceNumber?: string;
    newVehicleLicenceNumber?: string;
    email?: string;
};

export function useVehicleDataChangeForm() {
    const { idObject } = useParams();
    const dispatch = useDispatch();

    const { contractNumber } = userContracts.useGetContractByUrlParam();
    const verified = useSelector(getIsVerifiedUser);
    const isOneTimeChange = useSelector(user.getIsOneTimeChangeRole);
    const hasEditPermission = useSelector(user.hasPermission(PermissionTypes.EDIT_CONTRACT_DATA));

    const step = useSelector(getStep);
    const isFormLoading = useSelector(getIsFormLoading);
    const isSubmitting = useSelector(getIsSubmitting);

    const isAuthenticated = useHasRole(Roles.AUTHENTICATED);
    const holder = useSelector(getPolicyHolder);

    const vehicles = [];
    const vehiclesList = useSelector(getVehicles);
    vehiclesList.forEach((vehicle) => {
        const v = {
            code: vehicle.vinNumber,
            value: `${vehicle.manufacturer || ""} ${vehicle.model || ""}`,
            licencePlateNumber: vehicle.licencePlateNumber,
            vehicleLicenceNumber: vehicle.vehicleLicenceNumber,
        };
        vehicles.push(v);
    });

    const initialVehicle = vehicles.length === 1 ? vehicles[0] : null;

    const initialValues: VehicleDataChangeFormValues = {
        idChange: null,
        vinNumber: isAuthenticated ? initialVehicle.code : null,
        validityStartDate: new Date(),
        isLicencePlateNumberChange: false,
        isVehicleLicenceNumberChange: false,
        oldLicencePlateNumber: isAuthenticated ? initialVehicle.licencePlateNumber : null,
        newLicencePlateNumber: null,
        oldVehicleLicenceNumber: isAuthenticated ? initialVehicle.vehicleLicenceNumber : null,
        newVehicleLicenceNumber: null,
        email: isAuthenticated ? holder?.email || "" : null,
    };

    const validationSchema = getValidationSchema();

    const form = useForm<VehicleDataChangeFormValues>({
        defaultValues: initialValues,
        resolver: yupResolver(validationSchema),
        context: { isAuthenticated },
    });
    const { setValue, getValues } = form;

    const handleRequest = useFormRequest(form);

    useEffect(() => {
        function checkIfVerified() {
            if (!isOneTimeChange && (!hasEditPermission || !verified)) {
                dispatch(router.navigate(Pages.USER, Tabs.UNVERIFIED_USER, {}, { type: "form" }, false));
            }
        }

        const createChange = async () => {
            const onError = () => {
                return;
            };

            const onSuccess = (res) => {
                dispatch(setIsFormLoading(false));
                if (res.text) {
                    setValue("idChange", res.text);
                }
            };

            handleRequest(() => vehicleDataChange.createChange(idObject), onError, onSuccess);
        };

        checkIfVerified();

        isAuthenticated ? createChange() : dispatch(setIsFormLoading(false));

        return () => {
            dispatch(setStep(1));
            dispatch(setIsFormLoading(true));
            dispatch(setIsSubmitting(false));
            dispatch(setIsLicencePlateFillInLoading(false));
            dispatch(setIsVehicleLicenceFillInLoading(false));
        };
    }, []);

    async function onSubmit(data: VehicleDataChangeFormValues) {
        dispatch(setIsSubmitting(true));
        const dto = getSubmitDataDto(data);

        const onError = () => {
            dispatch(setIsSubmitting(false));
        };

        const onSuccess = (response) => {
            if (!isAuthenticated) {
                setValue("idChange", response.text);
            }

            dispatch(setStep(2));
            dispatch(setIsSubmitting(false));
        };

        isAuthenticated
            ? handleRequest(() => vehicleDataChange.submitAuthenticated(dto, getValues().idChange), onError, onSuccess)
            : handleRequest(() => vehicleDataChange.submitUnauthenticated(dto), onError, onSuccess);
    }

    function getSubmitDataDto(data: VehicleDataChangeFormValues): VehicleDataSubmitDto {
        return {
            newLicencePlateNumber: data.isLicencePlateNumberChange ? data.newLicencePlateNumber : null,
            newVehicleLicenceNumber: data.isVehicleLicenceNumberChange ? data.newVehicleLicenceNumber : null,
            oldLicencePlateNumber: data.isLicencePlateNumberChange ? data.oldLicencePlateNumber : null,
            oldVehicleLicenceNumber: data.isVehicleLicenceNumberChange ? data.oldVehicleLicenceNumber : null,
            vinNumber: data.vinNumber,
            validityStartDate: data.validityStartDate,
            idObject: Number(idObject),
        };
    }

    function previousStep() {
        dispatch(setStep(step - 1));
    }

    return {
        form,
        onSubmit,
        vehicles,
        step,
        previousStep,
        isFormLoading,
        isSubmitting,
        contractNumber,
    };
}
